import React, {
    useEffect,
    useRef,
    useState,
    type ChangeEvent,
    type FocusEvent,
    type KeyboardEvent,
} from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { cn } from "../../utils/lib";
import { ErrorMsg } from "./ErrorMsg";
import { Label } from "./Label";


interface CustomInputProps {
    label?: string;
    name: string;
    type?: string;
    value: string | number;
    placeholder?: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    isError?: boolean;
    errorMessage?: string;
    disabled?: boolean;
    required?: boolean;
    afterIcon?: React.ReactNode;
    beforeIcon?: React.ReactNode;
    allowFloat?: boolean;
    className?: string;
}

const CustomInput = ({
    label,
    name,
    type = "text",
    value,
    onChange,
    onBlur,
    isError = false,
    errorMessage,
    placeholder,
    disabled = false,
    required = false,
    afterIcon,
    beforeIcon,
    allowFloat = false,
    className = "",
}: CustomInputProps) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const inputType = type === "password" && isPasswordVisible ? "text" : type;

    useEffect(() => {
        const handleWheel = (e: WheelEvent) => {
            if (document.activeElement === inputRef.current) {
                e.preventDefault();
            }
        };

        const currentRef = inputRef.current;
        currentRef?.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            currentRef?.removeEventListener("wheel", handleWheel);
        };
    }, []);

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (type === "number") {
            if (
                event.key === "ArrowUp" ||
                event.key === "ArrowDown" ||
                ["e", "E", "+", "-"].includes(event.key) ||
                (!allowFloat && event.key === ".")
            ) {
                event.preventDefault();
            }
        }
    };

    return (
        <div className={cn("text-sm w-full 2xl:text-sm", className)}>
            {label && (
                <Label
                    text={label}
                    required={required}
                    className="mb-1.5 text-start 2xl:mb-2"
                />
            )}
            <div className="relative w-full">
                {beforeIcon && (
                    <div
                        className={cn(
                            "absolute left-1 top-1/2 -translate-y-1/2 p-2 rounded-lg",
                            disabled ? "bg-gray-100 text-gray-300" : "bg-white text-gray-400",
                        )}
                    >
                        {beforeIcon}
                    </div>
                )}
                <input
                    ref={inputRef}
                    type={inputType}
                    id={name}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    onKeyDown={handleKeyDown}
                    name={name}
                    disabled={disabled}
                    autoComplete="off"
                    placeholder={placeholder}
                    // required={required}
                    aria-invalid={isError}
                    aria-describedby={`${name}-error`}
                    className={cn(
                        "w-full rounded-lg border px-3 py-2 outline-none transition-colors duration-200 placeholder:text-gray-300 focus:border-primary focus:ring-1 focus:ring-primary",
                        isError ? "border-red-600" : "border-gray-300",
                        disabled
                            ? "cursor-not-allowed bg-gray-100 text-gray-500"
                            : "bg-white",
                        type === "password" && "pr-8",
                        beforeIcon && "pl-8",
                        afterIcon && "pr-8",
                    )}
                />
                {type === "password" && (
                    <div
                        className="absolute inset-y-0 right-3 flex cursor-pointer items-center justify-center active:scale-95"
                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                    >
                        {isPasswordVisible ? (
                            <FiEye className="text-primary w-4 h-4" />
                        ) : (
                            <FiEyeOff className=" w-4 h-4 text-gray-400" />
                        )}
                    </div>
                )}
                {afterIcon && (
                    <div
                        className={cn(
                            "absolute right-1 top-1/2 -translate-y-1/2 p-1",
                            disabled ? "bg-gray-100 text-gray-300" : "bg-white text-gray-400",
                        )}
                    >
                        {afterIcon}
                    </div>
                )}
            </div>
            {isError && errorMessage && <ErrorMsg text={errorMessage} />}
        </div>
    );
};

export { CustomInput };

